import axios from 'axios';
import * as Sentry from '@sentry/react';
import { FileUploader } from 'utils/upload/FileUploader';
import { logger } from 'utils/logger';
import { chatImageUploadFailed } from 'features/chat/actions';
import { ChatImageUploadPreparedItem, PendingChatImage } from 'features/chat/types';
import { PresignedUploadParams } from 'utils/upload/types';
import { store } from 'store/store';

const isAbortError = (error: unknown): boolean =>
  axios.isCancel(error) || (error instanceof Error && error.name === 'AbortError');

export class ChatImageUploadManager {
  private fileUploader = new FileUploader();

  private localIdsByMessageId = new Map<string, Set<string>>();

  upload = async (
    messageId: string,
    pending: PendingChatImage,
    prepared: ChatImageUploadPreparedItem
  ): Promise<void> => {
    this.track(messageId, pending.localId);

    const uploadParams: PresignedUploadParams = {
      strategy: 'presigned',
      storageUrl: prepared.external_storage_url,
      callbackUrl: prepared.callback_url,
      callbackToken: prepared.callback_token,
      callbackBody: {
        expiration: prepared.expiration_timestamp,
        file_id: prepared.file_id,
        file_name: prepared.file_name,
      },
    };

    try {
      await this.fileUploader.upload(pending.localId, pending.file, uploadParams, {
        fileName: pending.name,
      });
    } catch (error) {
      if (isAbortError(error)) {
        return;
      }

      Sentry.captureException(error);
      logger.remote().error('Chat image upload failed', error);
      store.dispatch(chatImageUploadFailed({ messageId }));
    } finally {
      this.untrack(messageId, pending.localId);
    }
  };

  cancelByMessageId = (messageId: string): void => {
    const localIds = this.localIdsByMessageId.get(messageId);
    if (!localIds) {
      return;
    }
    for (const localId of localIds) {
      this.fileUploader.cancel(localId);
    }
  };

  cancelAll = (): void => {
    this.fileUploader.cancelAll();
    this.localIdsByMessageId.clear();
  };

  private track(messageId: string, localId: string): void {
    let set = this.localIdsByMessageId.get(messageId);
    if (!set) {
      set = new Set();
      this.localIdsByMessageId.set(messageId, set);
    }
    set.add(localId);
  }

  private untrack(messageId: string, localId: string): void {
    const set = this.localIdsByMessageId.get(messageId);
    if (!set) {
      return;
    }
    set.delete(localId);
    if (set.size === 0) {
      this.localIdsByMessageId.delete(messageId);
    }
  }
}

export const chatImageUploadManager = new ChatImageUploadManager();
